import { useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { Header } from './components/common/Header.js'
import { NoticePage } from './components/common/NoticePage.js'
import { PermissionDenied } from './components/common/PermissionDenied.js'
import { RequireRole } from './components/common/RequireRole.js'
import { Button } from './components/ui/button.js'
import { loginUrlWithReturn } from './features/auth/api.js'
import { AccessGate } from './features/access-state/AccessGate.js'
import { SessionProvider } from './features/auth/SessionContext.js'
import { useSession } from './features/auth/SessionContext.js'
import { isEthisphereAdminOnly, VIEWER_ROLES } from './features/auth/permissions.js'
import { ImpersonationBanner } from './features/impersonation/ImpersonationBanner.js'
import { EthisphereAdminNotice } from './pages/EthisphereAdminNotice.js'
import { Unauthorized } from './pages/Unauthorized.js'
import { AppRoutes } from './routes/AppRoutes.js'

/**
 * Re-authentication is platform-owned, so hand the browser over rather than prompting
 * here. With no platform login configured there is nowhere to send them, so the notice
 * stands rather than a dead end.
 */
function SessionExpiredRedirect() {
  const target = loginUrlWithReturn(window.location.href)
  useEffect(() => {
    if (target) window.location.assign(target)
  }, [target])
  return target ? null : <Unauthorized />
}

/** A failed session fetch that isn't a 401 says nothing about the session, so retry in place. */
function SessionLoadError() {
  return (
    <NoticePage
      icon="⚠️"
      title="Something went wrong"
      action={<Button onClick={() => window.location.reload()}>Try again</Button>}
    >
      We couldn’t load your session. Check your connection and try again.
    </NoticePage>
  )
}

function AppShell() {
  const { session, loading, error } = useSession()

  if (loading) return null
  if (error === 'forbidden') return <PermissionDenied />
  if (error === 'unauthorized') return <SessionExpiredRedirect />
  if (error) return <SessionLoadError />
  if (isEthisphereAdminOnly(session?.user.roles)) return <EthisphereAdminNotice />

  return (
    <RequireRole roles={VIEWER_ROLES}>
      <ImpersonationBanner />
      <Header />
      <BrowserRouter>
        <AccessGate>
          <AppRoutes />
        </AccessGate>
      </BrowserRouter>
    </RequireRole>
  )
}

export default function App() {
  return (
    <SessionProvider>
      <AppShell />
    </SessionProvider>
  )
}
